<template>
  <div class="domain-detail-drawer" @click.self="$emit('close')">
    <div class="domain-detail-drawer__panel">
      <div class="domain-detail-drawer__header">
        <DomainRow :domain="domain" density="header" />
        <div class="domain-detail-drawer__header-right">
          <div v-if="domain.expires_at" class="domain-detail-drawer__expiry">
            <div class="domain-detail-drawer__expiry-figure">{{ expiryDate }}</div>
            <div class="domain-detail-drawer__expiry-sub">{{ expiryRel }}</div>
          </div>
          <button type="button" class="domain-detail-drawer__close" @click="$emit('close')">&#10005;</button>
        </div>
      </div>

      <div class="domain-detail-drawer__tabs">
        <div
          class="domain-detail-drawer__tab"
          :class="{ 'domain-detail-drawer__tab--active': tab === 'overview' }"
          @click="tab = 'overview'"
        >Overview</div>
        <div
          class="domain-detail-drawer__tab"
          :class="{ 'domain-detail-drawer__tab--active': tab === 'history' }"
          @click="tab = 'history'"
        >State history</div>
        <div
          class="domain-detail-drawer__tab"
          :class="{ 'domain-detail-drawer__tab--active': tab === 'errors' }"
          @click="tab = 'errors'"
        >Recent errors</div>
      </div>

      <div class="domain-detail-drawer__body">
        <div v-if="actionError" class="domain-detail-drawer__error">{{ actionError }}</div>

        <!-- ═══════════ Overview ═══════════ -->
        <template v-if="tab === 'overview'">
          <div v-if="isFlagged" class="domain-detail-drawer__review-banner">
            <div>
              <div class="domain-detail-drawer__review-title">Flagged for review</div>
              <div class="domain-detail-drawer__review-body">{{ domain.review_reason || 'A sync source stopped reporting this domain — could be a transfer-out or an API hiccup.' }}</div>
            </div>
            <div class="domain-detail-drawer__review-actions">
              <button type="button" class="domain-detail-drawer__btn" :disabled="busy" @click="resolveReview">Confirm &amp; resolve</button>
              <button type="button" class="domain-detail-drawer__btn" :disabled="busy" @click="$emit('recheck')">Re-check now</button>
            </div>
          </div>

          <div v-if="isManual" class="domain-detail-drawer__override-callout">
            <LockGlyph />
            <div>
              <div class="domain-detail-drawer__oc-title">Manual override — never re-derived</div>
              <div class="domain-detail-drawer__oc-body">
                Set to <strong>{{ stateLabelText }}</strong><span v-if="domain.usage_state_set_by_name"> by {{ domain.usage_state_set_by_name }}</span><span v-if="domain.usage_state_set_at"> on {{ setAtDate }}</span>.
                A manual override always wins and the sweep will not overwrite it.
              </div>
            </div>
          </div>

          <div class="domain-detail-drawer__field-grid">
            <div class="domain-detail-drawer__field"><div class="domain-detail-drawer__fl">Registrar</div><div class="domain-detail-drawer__fv">{{ registrar }}</div></div>
            <div class="domain-detail-drawer__field"><div class="domain-detail-drawer__fl">Discovered via</div><div class="domain-detail-drawer__fv">{{ viaLabel }}</div></div>
            <div class="domain-detail-drawer__field"><div class="domain-detail-drawer__fl">Zone ref</div><div class="domain-detail-drawer__fv domain-detail-drawer__fv--mono">{{ domain.zone_ref || '— no zone' }}</div></div>
            <div class="domain-detail-drawer__field"><div class="domain-detail-drawer__fl">Expires</div><div class="domain-detail-drawer__fv">{{ expiryDate ? `${expiryDate} · ${expiryRel}` : '— not supplied' }}</div></div>
            <div class="domain-detail-drawer__field"><div class="domain-detail-drawer__fl">Auto-renew</div><div class="domain-detail-drawer__fv">{{ autoRenewOn ? 'On' : 'Off' }}</div></div>
            <div class="domain-detail-drawer__field"><div class="domain-detail-drawer__fl">Usage state source</div><div class="domain-detail-drawer__fv">{{ isManual ? 'manual override' : 'derived' }}</div></div>
            <div class="domain-detail-drawer__field"><div class="domain-detail-drawer__fl">Linked site</div><div class="domain-detail-drawer__fv">{{ domain.site_id ? `Site #${domain.site_id}` : '— none' }}</div></div>
            <div class="domain-detail-drawer__field"><div class="domain-detail-drawer__fl">Abandoned by run</div><div class="domain-detail-drawer__fv">{{ domain.abandoned_by_run ? `Run #${domain.abandoned_by_run}` : '— not pipeline-abandoned' }}</div></div>
            <div class="domain-detail-drawer__field domain-detail-drawer__field--full"><div class="domain-detail-drawer__fl">Notes</div><div class="domain-detail-drawer__fv">{{ domain.notes || '— none on file' }}</div></div>
          </div>

          <div class="domain-detail-drawer__override-form">
            <div class="domain-detail-drawer__fl">Set usage state (manual override)</div>
            <div class="domain-detail-drawer__override-row">
              <span
                v-for="state in USAGE_STATES"
                :key="state"
                class="domain-detail-drawer__state-option"
                :class="{ 'domain-detail-drawer__state-option--picked': overridePick === state }"
                @click="overridePick = overridePick === state ? null : state"
              >
                <DomainStateChip :state="state" size="sm" />
              </span>
              <button
                type="button"
                class="domain-detail-drawer__btn domain-detail-drawer__btn--primary"
                :disabled="!overridePick || overridePick === domain.usage_state || busy"
                @click="applyOverride"
              >Apply</button>
            </div>
            <div class="domain-detail-drawer__override-hint">A manual override always wins and is never re-derived — the sweep will not touch this domain's state again.</div>
          </div>
        </template>

        <!-- ═══════════ Recent errors ═══════════ -->
        <template v-else-if="tab === 'errors'">
          <div class="domain-detail-drawer__errors-head">
            <p class="domain-detail-drawer__history-note">
              Last {{ recentErrors.length ? recentErrors.length : '' }} error &amp; critical log line{{ recentErrors.length === 1 ? '' : 's' }} tagged
              <code class="domain-detail-drawer__fv--mono">{{ domain.domain }}</code>, newest first.
            </p>
            <a
              v-if="recentErrorsServerId"
              class="domain-detail-drawer__errors-link"
              href="#"
              @click.prevent="viewInServerLogs"
            >view all in server logs &rarr;</a>
          </div>

          <div v-if="errorsLoading" class="domain-detail-drawer__tl-empty">Loading…</div>
          <div v-else-if="!recentErrors.length" class="domain-detail-drawer__tl-empty">
            No error log lines recorded for this domain. Per-vhost error tailing only appears once an enrolled server is shipping this domain's logs.
          </div>
          <div v-else class="domain-detail-drawer__errors-list">
            <div
              v-for="entry in recentErrors"
              :key="entry.id"
              class="domain-detail-drawer__err-line"
            >
              <div class="domain-detail-drawer__err-meta">
                <span class="domain-detail-drawer__err-lvl" :class="`lvl-${entry.severity}`">{{ String(entry.severity).toUpperCase() }}</span>
                <span class="domain-detail-drawer__err-time">{{ formatErrTime(entry.line_ts) }}</span>
                <span class="domain-detail-drawer__err-path">{{ entry.log_path }}</span>
              </div>
              <div class="domain-detail-drawer__err-text">{{ entry.line }}</div>
            </div>
          </div>
        </template>

        <!-- ═══════════ State history ═══════════ -->
        <template v-else>
          <p class="domain-detail-drawer__history-note">Reconstructed from the row's own timestamps — M3 keeps no separate event log.</p>
          <div class="domain-detail-drawer__timeline">
            <div
              v-for="entry in timeline"
              :key="entry.key"
              class="domain-detail-drawer__tl-entry"
              :class="{ 'domain-detail-drawer__tl-entry--manual': entry.manual }"
            >
              <div class="domain-detail-drawer__tl-ic"><component :is="entry.manual ? LockGlyph : DotGlyph" /></div>
              <div class="domain-detail-drawer__tl-body">
                <div class="domain-detail-drawer__tl-head">
                  <span class="domain-detail-drawer__tl-title">{{ entry.title }}</span>
                  <span class="domain-detail-drawer__tl-time">{{ entry.time }}</span>
                </div>
                <div class="domain-detail-drawer__tl-desc">{{ entry.desc }}</div>
              </div>
            </div>
            <div v-if="!timeline.length" class="domain-detail-drawer__tl-empty">No recorded events for this domain yet.</div>
          </div>
        </template>
      </div>

      <div class="domain-detail-drawer__footer">
        <span class="domain-detail-drawer__footer-note">
          {{ domain.last_synced_at ? `Last synced ${lastSyncedRel}` : 'Never seen by a sync — manual entry' }}
        </span>
        <div class="domain-detail-drawer__footer-actions">
          <button
            v-if="autoRenewOn"
            type="button"
            class="domain-detail-drawer__btn domain-detail-drawer__btn--primary"
            :disabled="busy"
            @click="disableAutoRenew"
          >Disable auto-renew</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// The per-domain detail drawer — `domains-register.html` §5, opened by a row
// click on the `/domains` register. Header chrome is the SAME canonical row
// component at `density="header"` (the design's one-row-component rule).
//
// Mockup deviations, both deliberate:
//  - "Edit notes" and "Re-derive (clears override)" are not built: the M3
//    API has no notes-update endpoint, and re-deriving a manual override
//    would break §4.6's one-way door (the backend enforces "a manual
//    override … is never re-derived", so the button cannot work).
//  - The State history tab synthesises its timeline from the row's own
//    timestamps (first seen / last sweep / manual override / review flag) —
//    M3 persists no per-domain event log to read from.
import { computed, defineComponent, h, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import useApi from '@apexl/icarus-core/src/services/api';
import DomainRow from '@root/components/Domains/DomainRow.vue';
import DomainStateChip from '@root/components/Domains/DomainStateChip.vue';
import {
  USAGE_STATES,
  discoveredViaLabel,
  expiryRelLabel,
  formatDate,
  formatDateTime,
  registrarLabel,
  relativeTime,
  stateLabel,
} from '@root/services/domainFormat';

const LockGlyph = defineComponent({
  name: 'LockGlyph',
  render: () => h('svg', { viewBox: '0 0 18 18', fill: 'none', stroke: 'currentColor', 'stroke-width': '1.6', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
    h('rect', { x: 4, y: 8, width: 10, height: 7, rx: 1.2 }),
    h('path', { d: 'M6 8V5.5a3 3 0 0 1 6 0V8' }),
  ]),
});

const DotGlyph = defineComponent({
  name: 'DotGlyph',
  render: () => h('svg', { viewBox: '0 0 18 18', fill: 'none', stroke: 'currentColor', 'stroke-width': '1.6', 'stroke-linecap': 'round' }, [
    h('path', { d: 'M3.5 9a5.5 5.5 0 0 1 9.3-4' }),
    h('path', { d: 'M12.8 2.5v3h-3' }),
    h('path', { d: 'M14.5 9a5.5 5.5 0 0 1-9.3 4' }),
    h('path', { d: 'M5.2 15.5v-3h3' }),
  ]),
});

const props = defineProps({
  domain: { type: Object, required: true },
  apiBase: { type: String, default: '/api/v1/domains' },
});

const emit = defineEmits(['close', 'updated', 'recheck']);

const api = useApi();
const router = useRouter();

const tab = ref('overview');
const overridePick = ref(null);
const busy = ref(false);
const actionError = ref(null);

// Recent-errors panel — fetched lazily the first time its tab is opened.
const recentErrors = ref([]);
const recentErrorsServerId = ref(null);
const errorsLoading = ref(false);
let errorsLoaded = false;

function formatErrTime(epochSeconds) {
  if (!epochSeconds) return '';
  return new Date(epochSeconds * 1000).toLocaleString([], { hour12: false });
}

async function loadRecentErrors() {
  if (errorsLoaded) return;
  errorsLoaded = true;
  errorsLoading.value = true;
  try {
    const data = await api.get(`${props.apiBase}/${props.domain.id}/recent-errors`);
    recentErrors.value = data.entries || [];
    recentErrorsServerId.value = data.server_id ?? null;
  } catch {
    recentErrors.value = [];
  } finally {
    errorsLoading.value = false;
  }
}

function viewInServerLogs() {
  if (recentErrorsServerId.value) {
    router.push(`/servers/${recentErrorsServerId.value}`);
  }
}

watch(tab, (t) => {
  if (t === 'errors') loadRecentErrors();
});

const isFlagged = computed(() => props.domain.review_flagged_at !== null && props.domain.review_flagged_at !== undefined);
const isManual = computed(() => props.domain.usage_state_source === 'manual');
const autoRenewOn = computed(() => !!Number(props.domain.auto_renew));
const registrar = computed(() => registrarLabel(props.domain));
const viaLabel = computed(() => discoveredViaLabel(props.domain.discovered_via));
const expiryDate = computed(() => formatDate(props.domain.expires_at));
const expiryRel = computed(() => expiryRelLabel(props.domain.expires_at));
const setAtDate = computed(() => formatDate(props.domain.usage_state_set_at));
const stateLabelText = computed(() => stateLabel(props.domain.usage_state));
const lastSyncedRel = computed(() => relativeTime(props.domain.last_synced_at));

const timeline = computed(() => {
  const entries = [];
  const d = props.domain;

  if (d.usage_state_set_at && isManual.value) {
    entries.push({
      key: 'override',
      at: d.usage_state_set_at,
      manual: true,
      title: `Manual override → ${stateLabel(d.usage_state)}`,
      time: formatDateTime(d.usage_state_set_at),
      desc: `${d.usage_state_set_by_name ? `By ${d.usage_state_set_by_name}. ` : ''}Overrides the derived state and will not be re-derived away by future sweeps.`,
    });
  }
  if (d.review_flagged_at) {
    entries.push({
      key: 'flag',
      at: d.review_flagged_at,
      manual: false,
      title: 'Flagged for review',
      time: formatDateTime(d.review_flagged_at),
      desc: d.review_reason || 'A sync source stopped reporting this domain.',
    });
  }
  if (d.last_synced_at) {
    entries.push({
      key: 'sync',
      at: d.last_synced_at,
      manual: false,
      title: 'Last reconciliation sweep',
      time: formatDateTime(d.last_synced_at),
      desc: 'Seen by the three-source sync — fields merged onto this row by domain name.',
    });
  }
  if (d.created) {
    entries.push({
      key: 'created',
      at: d.created,
      manual: false,
      title: `First seen — ${discoveredViaLabel(d.discovered_via)}`,
      time: formatDateTime(d.created),
      desc: d.discovered_via === 'manual'
        ? 'Row created by hand (manual add or CSV import).'
        : 'Row created by the reconciliation sweep.',
    });
  }

  return entries.sort((a, b) => b.at - a.at);
});

async function applyOverride() {
  busy.value = true;
  actionError.value = null;
  try {
    const data = await api.post(`${props.apiBase}/${props.domain.id}/usage-state`, { usage_state: overridePick.value });
    overridePick.value = null;
    emit('updated', data.domain);
  } catch (err) {
    actionError.value = err?.response?.data?.error || 'Could not apply the override.';
  } finally {
    busy.value = false;
  }
}

async function resolveReview() {
  busy.value = true;
  actionError.value = null;
  try {
    const data = await api.post(`${props.apiBase}/${props.domain.id}/review/resolve`, {});
    emit('updated', data.domain);
  } catch (err) {
    actionError.value = err?.response?.data?.error || 'Could not resolve the review flag.';
  } finally {
    busy.value = false;
  }
}

async function disableAutoRenew() {
  busy.value = true;
  actionError.value = null;
  try {
    await api.post(`${props.apiBase}/bulk`, { ids: [props.domain.id], action: 'disable_auto_renew' });
    const data = await api.get(`${props.apiBase}/${props.domain.id}`);
    emit('updated', data.domain);
  } catch (err) {
    actionError.value = err?.response?.data?.error || 'Could not disable auto-renew.';
  } finally {
    busy.value = false;
  }
}
</script>

<style lang="scss">
.domain-detail-drawer {
  position: fixed;
  inset: 0;
  background: rgba(14, 46, 48, .55);
  z-index: 1050;
  display: flex;
  justify-content: flex-end;

  &__panel {
    width: min(720px, 96vw);
    height: 100%;
    background: var(--theme-bg-card);
    box-shadow: var(--theme-shadow-panel);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &__header {
    padding: 20px 24px;
    border-bottom: 1px solid var(--theme-border-soft);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }

  &__header-right {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    flex-shrink: 0;
  }

  &__expiry {
    text-align: right;
  }

  &__expiry-figure {
    font-size: 16px;
    font-weight: 600;
    color: var(--theme-text-primary);
    font-variant-numeric: tabular-nums;
    font-family: var(--theme-font-mono);
  }

  &__expiry-sub {
    font-size: 11px;
    color: var(--theme-text-tertiary);
    margin-top: 2px;
  }

  &__close {
    width: 28px;
    height: 28px;
    border-radius: var(--theme-radius, 6px);
    border: 1px solid var(--theme-border);
    background: var(--theme-bg-card);
    color: var(--theme-text-tertiary);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 12px;
    line-height: 1;
  }

  &__tabs {
    display: flex;
    gap: 2px;
    padding: 0 24px;
    border-bottom: 1px solid var(--theme-border);
    background: var(--theme-bg-card);
    flex-shrink: 0;
  }

  &__tab {
    padding: 12px 4px;
    margin-right: 26px;
    font-size: 13px;
    font-weight: 600;
    color: var(--theme-text-tertiary);
    border-bottom: 2px solid transparent;
    cursor: pointer;

    &--active {
      color: var(--theme-accent-dark);
      border-bottom-color: var(--theme-accent-primary);
    }
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 22px 24px 24px;
    background: var(--theme-bg-content);
  }

  &__error {
    background: #f8dede;
    color: #a53232;
    border-radius: var(--theme-radius, 6px);
    padding: 8px 12px;
    font-size: 12px;
    margin-bottom: 14px;
  }

  &__review-banner {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 14px;
    border-radius: var(--theme-radius, 6px);
    background: #fbe1e1;
    margin-bottom: 18px;
    flex-wrap: wrap;
  }

  &__review-title {
    font-size: 12.5px;
    font-weight: 700;
    color: #a53232;
  }

  &__review-body {
    font-size: 12px;
    color: var(--theme-text-secondary);
    margin-top: 2px;
    line-height: 1.55;
  }

  &__review-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }

  &__override-callout {
    display: flex;
    gap: 10px;
    padding: 12px 14px;
    border-radius: var(--theme-radius, 6px);
    background: var(--theme-accent-light);
    margin-bottom: 18px;

    svg {
      width: 15px;
      height: 15px;
      color: var(--theme-accent-dark);
      flex-shrink: 0;
      margin-top: 1px;
    }
  }

  &__oc-title {
    font-size: 12.5px;
    font-weight: 700;
    color: var(--theme-accent-dark);
  }

  &__oc-body {
    font-size: 12px;
    color: var(--theme-text-secondary);
    margin-top: 2px;
    line-height: 1.55;
  }

  &__field-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px 24px;
    margin-bottom: 18px;
  }

  &__field--full {
    grid-column: 1 / -1;
  }

  &__fl {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .05em;
    color: var(--theme-text-tertiary);
    margin-bottom: 4px;
  }

  &__fv {
    font-size: 13px;
    color: var(--theme-text-primary);
    line-height: 1.5;

    &--mono {
      font-family: var(--theme-font-mono);
      font-size: 12px;
    }
  }

  &__override-form {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    padding: 14px 16px;
  }

  &__override-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 4px;
  }

  &__state-option {
    cursor: pointer;
    border-radius: 14px;
    padding: 2px;
    border: 1.5px solid transparent;

    &--picked {
      border-color: var(--theme-accent-primary);
    }
  }

  &__override-hint {
    font-size: 11px;
    color: var(--theme-text-tertiary);
    margin-top: 8px;
    line-height: 1.5;
  }

  &__history-note {
    font-size: 11.5px;
    color: var(--theme-text-tertiary);
    margin: 0 0 12px;
  }

  &__errors-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__errors-link {
    font-size: 11.5px;
    font-weight: 600;
    color: var(--theme-accent-dark);
    white-space: nowrap;
    flex-shrink: 0;
  }

  &__errors-list {
    background: var(--theme-bg-sidebar, #16302f);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    padding: 6px 0;
    font-family: var(--theme-font-mono);
  }

  &__err-line {
    padding: 8px 16px;
    border-top: 1px solid var(--theme-border-dark, rgba(255, 255, 255, .06));

    &:first-child {
      border-top: none;
    }
  }

  &__err-meta {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 3px;
  }

  &__err-lvl {
    font-size: 10px;
    font-weight: 700;

    &.lvl-error, &.lvl-critical { color: #e08080; }
    &.lvl-warning { color: #e0a030; }
    &.lvl-info { color: #7fb0d6; }
  }

  &__err-time {
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
  }

  &__err-path {
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__err-text {
    font-size: 11.5px;
    color: var(--theme-text-sidebar, #b9cbca);
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.5;
  }

  &__timeline {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    padding: 6px 20px;
  }

  &__tl-entry {
    display: flex;
    gap: 12px;
    padding: 14px 0;
    border-top: 1px solid var(--theme-border-soft);

    &:first-child {
      border-top: none;
    }

    &--manual .domain-detail-drawer__tl-ic {
      background: var(--theme-accent-light);
      color: var(--theme-accent-dark);
    }
  }

  &__tl-ic {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: var(--theme-border-soft);
    color: var(--theme-text-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 12px;
      height: 12px;
    }
  }

  &__tl-body {
    flex: 1;
    min-width: 0;
  }

  &__tl-head {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__tl-title {
    font-size: 12.5px;
    font-weight: 600;
  }

  &__tl-time {
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    font-family: var(--theme-font-mono);
    white-space: nowrap;
  }

  &__tl-desc {
    font-size: 12px;
    color: var(--theme-text-secondary);
    margin-top: 4px;
    line-height: 1.55;
  }

  &__tl-empty {
    padding: 18px 0;
    font-size: 12px;
    color: var(--theme-text-tertiary);
    text-align: center;
  }

  &__footer {
    padding: 14px 24px;
    border-top: 1px solid var(--theme-border-soft);
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: var(--theme-bg-card);
    flex-wrap: wrap;
    gap: 8px;
    flex-shrink: 0;
  }

  &__footer-note {
    font-size: 11px;
    color: var(--theme-text-tertiary);
  }

  &__footer-actions {
    display: flex;
    gap: 8px;
  }

  &__btn {
    font-size: 12.5px;
    font-weight: 600;
    padding: 8px 14px;
    border-radius: var(--theme-radius, 6px);
    cursor: pointer;
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);
    color: var(--theme-text-primary);
    white-space: nowrap;

    &--primary {
      background: var(--theme-accent-primary);
      border-color: var(--theme-accent-primary);
      color: #fff;
    }

    &:disabled {
      opacity: .6;
      cursor: default;
    }
  }
}
</style>
